// Contexto de entrega global.
//
// Guarda o local de entrega ativo da navegação: CEP, origem da definição, endereço quando
// houver, localidade e o resultado da resolução de atendimento.
//
// Três regras moram aqui, no reducer, e não nos chamadores — é o único jeito de valerem em
// toda transição:
//
// 1. CEP_INVALIDO não substitui o contexto vigente. Quem tem endereço ativo e digita um CEP
//    inexistente continua com o endereço; o erro aparece no campo. Perder o contexto por
//    erro de digitação é pior que o problema original.
// 2. Escolha manual nunca é sobrescrita por herança. Herança só age quando o estado é
//    INDEFINIDO.
// 3. Farmácia travada é uma segunda camada, independente do local: sobrevive à troca de CEP
//    e só sai por ação explícita do cliente.
//
// A persistência é feita em saga (store/sagas/deliveryContext.js), nunca aqui.

import { readDeliveryContext } from "../../util/deliveryContextStorage";

// Types
export const Types = {
  HYDRATE: "deliveryContext/HYDRATE",
  APPLY_CEP: "deliveryContext/APPLY_CEP",
  SELECT_ADDRESS: "deliveryContext/SELECT_ADDRESS",
  SET_FROM_INHERITANCE: "deliveryContext/SET_FROM_INHERITANCE",
  RESOLVE_REQUEST: "deliveryContext/RESOLVE_REQUEST",
  RESOLVE_SUCCESS: "deliveryContext/RESOLVE_SUCCESS",
  RESOLVE_FAILURE: "deliveryContext/RESOLVE_FAILURE",
  CEP_INVALIDO: "deliveryContext/CEP_INVALIDO",
  CLEAR_CEP_ERROR: "deliveryContext/CLEAR_CEP_ERROR",
  SET_LOCKED_PHARMACY: "deliveryContext/SET_LOCKED_PHARMACY",
  ADDRESS_REMOVED: "deliveryContext/ADDRESS_REMOVED",
  ADDRESS_CHANGED: "deliveryContext/ADDRESS_CHANGED",
  SYNC_FROM_STORAGE: "deliveryContext/SYNC_FROM_STORAGE",
  DESTACAR_SELETOR: "deliveryContext/DESTACAR_SELETOR",
  CLEAR_LOCATION: "deliveryContext/CLEAR_LOCATION",
  CLEAR: "deliveryContext/CLEAR"
};

export const Status = {
  INDEFINIDO: "INDEFINIDO",
  RESOLVENDO: "RESOLVENDO",
  ATENDIDO: "ATENDIDO",
  NAO_ATENDIDO: "NAO_ATENDIDO",
  FALHA: "FALHA",
  CEP_INVALIDO: "CEP_INVALIDO"
};

export const Origem = {
  MANUAL_CEP: "MANUAL_CEP",
  MANUAL_ENDERECO: "MANUAL_ENDERECO",
  HERDADO_PRIMEIRO: "HERDADO_PRIMEIRO",
  HERDADO_PEDIDO: "HERDADO_PEDIDO"
};

// Valor cru devolvido pela API. LOCALIDADE_INCOMPLETA não é ausência de cobertura: é CEP
// que resolveu sem bairro, com nenhuma loja modelo C atendendo. A mensagem pede o endereço
// completo, e é por isso que o valor cru sobrevive ao lado do status.
export const Cobertura = {
  ATENDIDO: "ATENDIDO",
  NAO_ATENDIDO: "NAO_ATENDIDO",
  LOCALIDADE_INCOMPLETA: "LOCALIDADE_INCOMPLETA"
};

// Initial State
const BASE_STATE = {
  status: Status.INDEFINIDO,
  cep: null,
  origem: null,
  enderecoId: null,
  localidade: null,
  cobertura: null,
  resolucao: null,
  resolvidoEm: null,
  definidoEm: null,
  farmaciaTravada: null,
  conflitoFarmacia: false,
  erroCep: null,
  // Instante do ultimo pedido de destaque no seletor da barra superior. Nao persistido: e
  // sinal de interface, com vida de milissegundos.
  destaqueEm: null,
  hidratado: false
};

// Leitura síncrona: o boot precisa do CEP antes de qualquer saga rodar, senão o seletor
// pisca "informe seu CEP" para quem já tem contexto.
function buildInitialState() {
  const persistido = readDeliveryContext();
  if (!persistido) return { ...BASE_STATE, hidratado: true };

  return {
    ...BASE_STATE,
    hidratado: true,
    cep: persistido.cep,
    origem: persistido.origem,
    enderecoId: persistido.enderecoId,
    localidade: persistido.localidade,
    // Resolução vencida entra como RESOLVENDO: o CEP fica, só a resolução é refeita.
    status: persistido.resolucaoVencida
      ? Status.RESOLVENDO
      : statusFromCobertura(persistido.cobertura),
    cobertura: persistido.resolucaoVencida ? null : persistido.cobertura,
    resolucao: persistido.resolucaoVencida ? null : persistido.resolucao,
    resolvidoEm: persistido.resolucaoVencida ? null : persistido.resolvidoEm,
    definidoEm: persistido.definidoEm
  };
}

function statusFromCobertura(cobertura) {
  if (cobertura === Cobertura.ATENDIDO) return Status.ATENDIDO;
  if (!cobertura) return Status.INDEFINIDO;
  // NAO_ATENDIDO e LOCALIDADE_INCOMPLETA compartilham o status; quem diferencia a mensagem
  // é o campo `cobertura`.
  return Status.NAO_ATENDIDO;
}

// A farmácia travada cobre o CEP? Cobrir sem ser a mais rápida não é conflito — o prazo é o
// dela, e foi isso que o cliente escolheu. Os dados de cada modalidade ja chegam agrupados
// em entregaNormal e entregaExpressa; o front nao precisa recombinar campos achatados.
function calcularConflito(farmaciaTravada, resolucao) {
  if (!farmaciaTravada || !resolucao) return false;

  const atendeEntrega = (resolucao.entrega?.lojas || []).some(
    (loja) => loja.loja_id === farmaciaTravada.id
  );
  const atendeRetirada = (resolucao.retirada?.lojas || []).some(
    (loja) => loja.loja_id === farmaciaTravada.id
  );

  return !atendeEntrega && !atendeRetirada;
}

const INITIAL_STATE = buildInitialState();

// Local novo: zera resolução e erro, mantém a camada da farmácia travada.
function aplicarLocal(state, { cep, origem, enderecoId, localidade }) {
  return {
    ...state,
    status: Status.RESOLVENDO,
    cep,
    origem,
    enderecoId: enderecoId ?? null,
    localidade: localidade ?? null,
    cobertura: null,
    resolucao: null,
    resolvidoEm: null,
    definidoEm: Date.now(),
    conflitoFarmacia: false,
    erroCep: null
  };
}

// Reducer
export default function deliveryContext(state = INITIAL_STATE, action) {
  switch (action.type) {
    case Types.APPLY_CEP:
      return aplicarLocal(state, {
        cep: action.payload.cep,
        origem: Origem.MANUAL_CEP,
        enderecoId: null,
        localidade: action.payload.localidade
      });

    case Types.SELECT_ADDRESS:
      return aplicarLocal(state, {
        cep: action.payload.cep,
        origem: Origem.MANUAL_ENDERECO,
        enderecoId: action.payload.enderecoId,
        localidade: action.payload.localidade
      });

    // Precedência: herança só age no vazio. Escolha manual nunca é sobrescrita.
    case Types.SET_FROM_INHERITANCE: {
      if (state.status !== Status.INDEFINIDO) return state;
      return aplicarLocal(state, {
        cep: action.payload.cep,
        origem: action.payload.origem,
        enderecoId: action.payload.enderecoId,
        localidade: action.payload.localidade
      });
    }

    case Types.RESOLVE_REQUEST:
      // Reresolução do mesmo local (TTL vencido, farmácia destravada): preserva tudo o que
      // já foi decidido e só marca que a consulta está em curso.
      return { ...state, status: Status.RESOLVENDO, erroCep: null };

    case Types.RESOLVE_SUCCESS: {
      const { cobertura, localidade, resolucao } = action.payload;
      return {
        ...state,
        status: statusFromCobertura(cobertura),
        cobertura,
        // A API devolve a localidade normalizada; sem ela, mantém a que já havia.
        localidade: localidade || state.localidade,
        resolucao,
        resolvidoEm: Date.now(),
        conflitoFarmacia: calcularConflito(state.farmaciaTravada, resolucao),
        erroCep: null
      };
    }

    case Types.RESOLVE_FAILURE:
      // Falha de rede ou de servidor. Distinta de NAO_ATENDIDO, que chega em HTTP 200 com
      // indicador no corpo. Preserva CEP e localidade: o cliente não perde o local porque a
      // API caiu, e resolvidoEm não é tocado para que a próxima tentativa seja imediata.
      return { ...state, status: Status.FALHA, erroCep: null };

    case Types.CEP_INVALIDO:
      return {
        ...state,
        erroCep: action.payload.mensagem,
        // Só assume CEP_INVALIDO quem não tinha contexto para perder.
        status: state.cep ? state.status : Status.CEP_INVALIDO
      };

    case Types.CLEAR_CEP_ERROR:
      if (!state.erroCep && state.status !== Status.CEP_INVALIDO) return state;
      return {
        ...state,
        erroCep: null,
        status:
          state.status === Status.CEP_INVALIDO ? Status.INDEFINIDO : state.status
      };

    // Camada separada: não toca em CEP, endereço nem localidade.
    case Types.SET_LOCKED_PHARMACY: {
      const farmaciaTravada = action.payload.farmacia;
      return {
        ...state,
        farmaciaTravada,
        conflitoFarmacia: calcularConflito(farmaciaTravada, state.resolucao)
      };
    }

    case Types.SYNC_FROM_STORAGE: {
      const contexto = action.payload.contexto;
      if (!contexto) {
        return { ...BASE_STATE, hidratado: true, farmaciaTravada: state.farmaciaTravada };
      }
      return {
        ...state,
        status: statusFromCobertura(contexto.cobertura),
        cep: contexto.cep,
        origem: contexto.origem,
        enderecoId: contexto.enderecoId,
        localidade: contexto.localidade,
        cobertura: contexto.cobertura,
        resolucao: contexto.resolucao,
        resolvidoEm: contexto.resolvidoEm,
        definidoEm: contexto.definidoEm,
        conflitoFarmacia: calcularConflito(state.farmaciaTravada, contexto.resolucao),
        erroCep: null
      };
    }

    // Destaque temporario do seletor da barra, pedido quando a alteracao parte do card da
    // pagina de produto: a mudanca acontece fora do campo de visao do cliente.
    //
    // Precisa ser uma acao explicita, e nao um booleano derivado da troca de CEP, senao o
    // destaque tambem dispararia quando a troca parte do proprio seletor, que esta a vista.
    case Types.DESTACAR_SELETOR:
      return { ...state, destaqueEm: Date.now() };

    // Zera so a camada de local. A farmacia travada sobrevive: ela e independente do
    // endereco e so sai por acao explicita do cliente.
    case Types.CLEAR_LOCATION:
      return {
        ...BASE_STATE,
        hidratado: true,
        farmaciaTravada: state.farmaciaTravada,
        conflitoFarmacia: false
      };

    case Types.CLEAR:
      // Logout limpa as duas camadas.
      return { ...BASE_STATE, hidratado: true };

    default:
      return state;
  }
}

// Action Creators
export const Creators = {
  hydrate: () => ({ type: Types.HYDRATE }),

  applyCep: (cep, localidade = null) => ({
    type: Types.APPLY_CEP,
    payload: { cep, localidade }
  }),

  selectAddress: (enderecoId, cep, localidade = null) => ({
    type: Types.SELECT_ADDRESS,
    payload: { enderecoId, cep, localidade }
  }),

  setFromInheritance: ({ cep, origem, enderecoId, localidade }) => ({
    type: Types.SET_FROM_INHERITANCE,
    payload: { cep, origem, enderecoId, localidade }
  }),

  resolveRequest: () => ({ type: Types.RESOLVE_REQUEST }),

  resolveSuccess: ({ cobertura, localidade, resolucao }) => ({
    type: Types.RESOLVE_SUCCESS,
    payload: { cobertura, localidade, resolucao }
  }),

  resolveFailure: (error) => ({
    type: Types.RESOLVE_FAILURE,
    payload: { error }
  }),

  cepInvalido: (mensagem = "CEP não encontrado. Confira e tente novamente.") => ({
    type: Types.CEP_INVALIDO,
    payload: { mensagem }
  }),

  // Chamado ao abrir o painel e a cada digitação no campo: erro antigo não reaparece.
  clearCepError: () => ({ type: Types.CLEAR_CEP_ERROR }),

  setLockedPharmacy: (farmacia) => ({
    type: Types.SET_LOCKED_PHARMACY,
    payload: { farmacia }
  }),

  addressRemoved: (enderecoId) => ({
    type: Types.ADDRESS_REMOVED,
    payload: { enderecoId }
  }),

  addressChanged: (endereco) => ({
    type: Types.ADDRESS_CHANGED,
    payload: { endereco }
  }),

  syncFromStorage: (contexto) => ({
    type: Types.SYNC_FROM_STORAGE,
    payload: { contexto }
  }),

  destacarSeletor: () => ({ type: Types.DESTACAR_SELETOR }),

  clearLocation: () => ({ type: Types.CLEAR_LOCATION }),

  clear: () => ({ type: Types.CLEAR })
};

// Selectors

// Farmácia cujo prazo e custo valem para o contexto: a travada, quando cobre o CEP;
// a eleita pela API, caso contrário. A API já comparou NORMAL e EXPRESSA — o front lê
// modalidade_mais_rapida, não recalcula.
export function selectFarmaciaDoContexto(state) {
  const { farmaciaTravada, conflitoFarmacia, resolucao } = state.deliveryContext;
  if (!resolucao) return null;

  const lojas = resolucao.entrega?.lojas || [];

  if (farmaciaTravada) {
    if (conflitoFarmacia) return null; // prazo suprimido, farmácia mantida
    return lojas.find((loja) => loja.loja_id === farmaciaTravada.id) || null;
  }

  return lojas.find((loja) => loja.loja_id === resolucao.entrega?.eleita_loja_id) || null;
}

// Desconto do Pix online da farmácia do contexto (P-113): a escolhida pelo cliente (travada) ou, sem ela, a eleita
// para entrega e, sem entrega, a de retirada. Sem farmácia conhecida, `conhecida` é false e a vitrine mostra "até X% OFF no Pix" com o maior desconto da
// empresa (`percMax`). Usar com shallowEqual no useSelector.
export function selectDescontoPixDoContexto(state) {
  const { resolucao, farmaciaTravada } = state.deliveryContext;
  const percMax = Number(state.store?.data?.empresa?.perc_desconto_pix_max) || 0;
  if (!resolucao) return { conhecida: false, perc: 0, percMax };

  const entrega = resolucao.entrega?.lojas || [];
  const retirada = resolucao.retirada?.lojas || [];
  let loja;
  if (farmaciaTravada) {
    // Farmácia escolhida pelo cliente: vale o desconto dela, mesmo fora do CEP (conflito); fora da resolução, a loja
    // não é conhecida e a vitrine mostra "até X%"
    loja =
      entrega.find((item) => item.loja_id === farmaciaTravada.id) ||
      retirada.find((item) => item.loja_id === farmaciaTravada.id) ||
      null;
  } else {
    loja =
      entrega.find((item) => item.loja_id === resolucao.entrega?.eleita_loja_id) ||
      retirada.find((item) => item.loja_id === resolucao.retirada?.eleita_loja_id) ||
      null;
  }

  return { conhecida: !!loja, lojaId: loja?.loja_id || null, perc: Number(loja?.perc_desconto_pix) || 0, percMax };
}

// Rótulo do seletor. Bairro e cidade — o modelo não tem apelido de endereço, e nome_contato
// identifica pessoa, não lugar. Ver docs/contexto-entrega/rotulo-endereco.md.
export function selectRotuloLocal(state) {
  const { localidade } = state.deliveryContext;
  if (!localidade) return null;

  const partes = [localidade.bairro, localidade.cidade].filter(Boolean);
  return partes.length > 0 ? partes.join(" · ") : null;
}
